import { Fragment } from "react";

import clsx from "clsx";
import { useLocation } from "react-router-dom";

import { ControlBar } from "@components/control-bar";
import { ExpandableBreakdownSelector } from "@components/reports/breakdown-selector";
import { StuckToTop } from "@components/stuck-to-top";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  DateRangeChoiceEnum,
  type Breakdown,
  type CategoricalBreakdownConfig,
  type DateRangeChoiceV2,
  type FixedDateInterval,
} from "@north-beam/nb-common";
import { formatDateRangeText } from "@shared/date-range-picker";
import { AttributionModelSelect } from "@shared/selects";
import { DateRangeSelect } from "@shared/selects/date-range-select";
import { renderChevronAnimation } from "@shared/selects/utils";

import { BreakdownSelect } from "./breakdown-select";
import { PrintPageButton } from "./print-page-button";
import {
  WindowSizeDays,
  type IWindowSizeDays,
  type ReportBodyState,
} from "./use-report-body-state";
import { WindowSizeDaysSelect } from "./window-size-days-select";

interface ReportBodyControlProps {
  isLoading: boolean;
  state: ReportBodyState;
  breakdownConfigs: CategoricalBreakdownConfig[];
  setFilters: (filters: Breakdown[]) => void;
  setAttributionModel: (value: string) => void;
  setDateRange: (value: FixedDateInterval | DateRangeChoiceV2) => void;
  setWindowSizeDays: (value: IWindowSizeDays) => void;
  setBreakdown: (value: string) => void;
}

export function ReportBodyControl({
  isLoading,
  state,
  breakdownConfigs,
  setFilters,
  setAttributionModel,
  setDateRange,
  setWindowSizeDays,
  setBreakdown,
}: ReportBodyControlProps) {
  const { search } = useLocation();

  const { attributionModel, filters, dateRange, windowSizeDays, breakdown } =
    state;

  const dateRangeCurrent =
    typeof dateRange === "string"
      ? DateRangeChoiceEnum.toReactSelect(dateRange)
      : dateRange;

  return (
    <StuckToTop>
      {() => (
        <ControlBar>
          <div
            className="flex space-x-2 max-w-7xl"
            style={{ flex: "3 3 auto" }}
          >
            <AttributionModelSelect
              isLoading={isLoading}
              attributionModel={attributionModel}
              onChange={setAttributionModel}
            />
            <ExpandableBreakdownSelector
              label="FILTERS"
              current={filters}
              onUpdate={setFilters}
              allConfigs={breakdownConfigs}
              available={breakdownConfigs}
              isLoading={isLoading}
            />
            <BreakdownSelect
              isLoading={isLoading}
              breakdown={breakdown}
              breakdownConfigs={breakdownConfigs}
              onChange={setBreakdown}
            />
            <Popover className="nb-input-container relative">
              <label className="label">DATE FILTER</label>
              <PopoverButton
                className="nb-input flex flex-col items-start mr-2 text-sm min-w-[12rem] w-max"
                disabled={isLoading}
              >
                {({ open }) => (
                  <div className="flex items-center space-between w-full">
                    <div className="flex-grow text-left mr-4">
                      {formatDateRangeText(dateRangeCurrent)}, by{" "}
                      {WindowSizeDays.getLabel(windowSizeDays)}
                    </div>
                    {renderChevronAnimation(open)}
                  </div>
                )}
              </PopoverButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <PopoverPanel
                  className={clsx(
                    "absolute left-0 z-100 mt-2 elevation-1 rounded-md",
                    "p-3 w-72 bg-white flex flex-col space-y-2",
                  )}
                >
                  <WindowSizeDaysSelect
                    isLoading={isLoading}
                    windowSizeDays={windowSizeDays}
                    onChange={setWindowSizeDays}
                  />
                  <DateRangeSelect
                    isLoading={isLoading}
                    dateRange={dateRange}
                    onUpdate={setDateRange}
                  />
                </PopoverPanel>
              </Transition>
            </Popover>
          </div>
          <PrintPageButton search={search} />
        </ControlBar>
      )}
    </StuckToTop>
  );
}
